import { CSSProperties, FC, HTMLProps, ReactNode } from 'react'

import { cn } from 'src/lib/cn'
import { cva, VariantProps } from 'src/lib/cva'

export interface TypographyProps extends HTMLProps<HTMLDivElement> {
  children: ReactNode
  component?: 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5'
  fontSize?: CSSProperties['fontSize']
  marginTop?: CSSProperties['marginTop']
  marginLeft?: CSSProperties['marginLeft']
  marginRight?: CSSProperties['marginRight']
  marginBottom?: CSSProperties['marginBottom']
  paddingLeft?: CSSProperties['paddingLeft']
  paddingRight?: CSSProperties['paddingRight']
  paddingTop?: CSSProperties['paddingTop']
  paddingBottom?: CSSProperties['paddingBottom']
  fontWeight?: CSSProperties['fontWeight']
}

const typographyVariants = cva('inline-flex', {
  variants: {
    variant: {
      label: 'text-sm font-medium text-oslo-gray',
      title1: 'text-base font-bold text-mine-shaft',
      title2: 'text-sm font-semibold text-dove-gray',
      body: 'text-sm font-normal text-dove-gray',
      modalTitle: 'text-xl font-semibold text-outer-space',
      dropdownLabel: 'text-sm font-semibold leading-[15px] text-mine-shaft uppercase',
      sectionSeparatorTitle:
        'text-xs font-medium text-grey uppercase text-nowrap select-none flex items-center',
      sectionTitle: 'text-xs font-normal text-hit-gray uppercase',
    },
  },
})

const Typography: FC<TypographyProps & VariantProps<typeof typographyVariants>> = ({
  variant,
  className,
  component,
  children,
  fontSize,
  style,
  marginTop,
  marginLeft,
  marginBottom,
  marginRight,
  fontWeight,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingTop,
  ...divProps
}) => {
  const classes = cn(typographyVariants({ variant }), className)

  const Component = component || 'div'

  return (
    <Component
      style={{
        ...style,
        marginLeft,
        marginTop,
        marginBottom,
        marginRight,
        fontSize,
        paddingBottom,
        paddingLeft,
        paddingRight,
        paddingTop,
        fontWeight,
      }}
      className={classes}
      {...divProps}
    >
      {children}
    </Component>
  )
}

export default Typography
